import {
    Box,
    Button,
    Typography,
    Grid,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';
import {
    useAsignarTurnoExamenEmpleadosMutation
} from '../../../services/periodicosApi';
import dayjs from 'dayjs';
import DateTimePickerComponent from '../../../components/DateTimePickerComponent';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
interface PeriodicosDrawerProps {
    selectedIds: string[];
    setOpen: (state: string) => void;
    reloadGrid: () => void;
    selectedEmployee: any;
    selectedEmployees?: any[];
    reloadCurrentTab: () => void;
    tabIndex: number;
    reconfirmation: boolean;
    handleAssignShift?: (turnDate: any, selectedEmployees: any[]) => Promise<boolean>;
    isAssigningShift?: boolean;
}
const PeriodicosAsignarTurnoDrawer = ({
    selectedIds,
    setOpen,
    reloadGrid,
    selectedEmployee,
    selectedEmployees = [],
    reloadCurrentTab,
    tabIndex,
    reconfirmation = false,
    handleAssignShift,
    isAssigningShift = false
}: PeriodicosDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const [fechaTurno, setFechaTurno] = useState<any>(null);
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const [asignarTurno, { isLoading }] = useAsignarTurnoExamenEmpleadosMutation();
    const parsedIdsSelection = selectedIds
        .map((id: string) => Number(String(id).split('-')[0]))
        .filter((id: number) => Number.isFinite(id) && id > 0)
        .map((id: number) => ({ idExamenEmpleado: id }));

    const effectiveSelectedEmployees =
        selectedEmployees.length > 0
            ? selectedEmployees
            : selectedEmployee
            ? [selectedEmployee]
            : parsedIdsSelection;

    const isMassiveAssignment = effectiveSelectedEmployees.length > 1;


    const isReadOnly = selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto === 5;

    const handleCloseDrawer = () => {
        setOpen('');
        if (tabIndex === 3) {
            reloadGrid();
        }
    };

    const existingShiftDate =
        selectedEmployee?.examenEmpleado?.fechaHoraTurno ||
        selectedEmployee?.turno ||
        selectedEmployee?.turnoAsignado ||
        null;

    useEffect(() => {
        if (existingShiftDate) {
            const parsedFecha = dayjs(existingShiftDate, 'YYYY-MM-DDTHH:mm:ss');
            setFechaTurno(parsedFecha.isValid() ? parsedFecha : null);
        }
    }, [selectedEmployee]);

    const handleSave = async () => {
        if (!fechaTurno) {
            enqueueSnackbar(t('drawer.messages.missingData'), { variant: 'warning' });
            return;
        }

        const fechaTurnoIso = dayjs(fechaTurno).toISOString();

        if (!handleAssignShift) {
            const idExamenEmpleados = selectedIds
                .map((id: string) => Number(String(id).split('-')[0]))
                .filter((id: number) => Number.isFinite(id) && id > 0);

            if (!idExamenEmpleados.length) {
                enqueueSnackbar('No se encontraron empleados válidos para asignar turno.', {
                    variant: 'warning'
                });
                return;
            }

            try {
                const response: any = await asignarTurno({
                    idExamenEmpleados,
                    fechaTurno: fechaTurnoIso
                }).unwrap();

                if (response?.error) {
                    enqueueSnackbar(t('drawer.messages.assignErrorTurn'), { variant: 'error' });
                    return;
                }

                enqueueSnackbar(t('drawer.messages.assignSuccessTurn'), { variant: 'success' });
                reloadGrid();
                setOpen('');
            } catch (error) {
                dispatch(
                    showAlert({
                        title: 'No se pudo asignar el turno',
                        message: ERROR_CONTACT_SUPPORT,
                        icon: 'error',
                        confirmText: ALERT_CONFIRM_TEXT
                    })
                );
            }
            return;
        }

        const success = await handleAssignShift(fechaTurnoIso, effectiveSelectedEmployees);
        if (success) {
            reloadGrid();
            setOpen('');
        }
    };

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Grid container flexDirection="column">
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={
                        isMassiveAssignment
                            ? 'Asignar Turno Masivo'
                            : existingShiftDate
                            ? 'Reasignar Turno Individual'
                            : 'Asignar Turno Individual'
                    }
                />

                {!isMassiveAssignment && (
                    <Box
                        sx={{
                            mt: 4,
                            p: 2,
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            backgroundColor: '#FFFFFF'
                        }}
                    >
                        <Typography variant="body1" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                            {[selectedEmployee?.apellido, selectedEmployee?.nombre]
                                .filter(p => p && p !== '-')
                                .join(', ') || '-'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                            {`CUIL: ${selectedEmployee?.cuil || '-'}`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                            {selectedEmployee?.puesto || '-'}
                        </Typography>
                    </Box>
                )}

                    <Typography sx={{ mt: 4, mb: 1, fontSize: '0.95rem', color: '#475569' }}>
                    {isMassiveAssignment
                        ? `Asignando turno a ${effectiveSelectedEmployees.length} empleados seleccionados.`
                        : 'Asigná fecha y hora para este empleado.'}
                    </Typography>

                {!isMassiveAssignment && reconfirmation && (
                    <>
                        <Typography
                            sx={{
                                fontSize: '1rem',
                                font: 'Roboto',
                                fontWeight: '400'
                            }}
                        >
                            {selectedEmployee?.examenReconfirmacion || '-'}
                        </Typography>
                    </>
                )}

                <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 4 }}>
                    Día y hora del turno
                </Typography>

                <DateTimePickerComponent
                    label={t('drawer.turno.title')}
                    value={fechaTurno}
                    onChange={setFechaTurno}
                    disabled={isReadOnly}
                    disablePast
                    sx={{ mt: 2, width: '300px' }}
                />
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseDrawer} sx={{ mr: 1 }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!fechaTurno || isLoading || isAssigningShift}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {isLoading || isAssigningShift ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        'Confirmar'
                    )}
                </Button>
            </Box>
        </Box>
    );
};
export default PeriodicosAsignarTurnoDrawer;
